import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useApp } from '../../context/AppContext';
import { loadPDF, mergePDFs, downloadPDF, getPageCount } from '../../utils/pdfUtils';
import './PDFMerger.css';

const PDFMerger = () => {
  const { showNotification, setCurrentView } = useApp();
  const [files, setFiles] = useState([]);
  const [isMerging, setIsMerging] = useState(false);
  const [outputFilename, setOutputFilename] = useState('merged');

  const onDrop = useCallback(async (acceptedFiles) => {
    const pdfFiles = acceptedFiles.filter(file => file.type === 'application/pdf');

    if (pdfFiles.length === 0) {
      showNotification('Please select PDF files', 'warning');
      return;
    }

    const newFiles = await Promise.all(
      pdfFiles.map(async (file) => {
        try {
          const pdfDoc = await loadPDF(file);
          return {
            id: Date.now() + Math.random(),
            file,
            pdfDoc,
            name: file.name,
            pageCount: getPageCount(pdfDoc)
          };
        } catch (error) {
          console.error(`Error loading ${file.name}:`, error);
          showNotification(`Failed to load ${file.name}`, 'error');
          return null;
        }
      })
    );

    setFiles(prev => [...prev, ...newFiles.filter(Boolean)]);
  }, [showNotification]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true
  });

  const handleRemoveFile = (id) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    setFiles(prev => {
      const newFiles = [...prev];
      [newFiles[index - 1], newFiles[index]] = [newFiles[index], newFiles[index - 1]];
      return newFiles;
    });
  };

  const handleMoveDown = (index) => {
    if (index === files.length - 1) return;
    setFiles(prev => {
      const newFiles = [...prev];
      [newFiles[index], newFiles[index + 1]] = [newFiles[index + 1], newFiles[index]];
      return newFiles;
    });
  };

  const handleMerge = async () => {
    if (files.length < 2) {
      showNotification('Please add at least 2 PDF files to merge', 'warning');
      return;
    }

    setIsMerging(true);

    try {
      const pdfDocs = files.map(f => f.pdfDoc);
      const mergedDoc = await mergePDFs(pdfDocs);
      const filename = `${outputFilename || 'merged'}.pdf`;
      await downloadPDF(mergedDoc, filename);
      showNotification('PDFs merged successfully!', 'success');
    } catch (error) {
      console.error('Error merging PDFs:', error);
      showNotification('Failed to merge PDFs', 'error');
    }

    setIsMerging(false);
  };

  const totalPages = files.reduce((sum, f) => sum + f.pageCount, 0);

  return (
    <div className="merger-container">
      <div className="merger-header">
        <button className="btn btn-ghost" onClick={() => setCurrentView('upload')}>
          ← Back
        </button>
        <h2>Merge PDFs</h2>
        <div className="merger-stats">
          {files.length} file{files.length !== 1 ? 's' : ''} ({totalPages} pages)
        </div>
      </div>

      <div className="merger-content">
        <div
          {...getRootProps()}
          className={`merger-dropzone ${isDragActive ? 'active' : ''}`}
        >
          <input {...getInputProps()} />
          <div className="dropzone-content">
            <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>{isDragActive ? 'Drop PDF files here' : 'Drag & drop PDF files here, or click to select'}</p>
            <span>You can add multiple files</span>
          </div>
        </div>

        {files.length > 0 && (
          <div className="files-list-section">
            <h3>Files to Merge (drag to reorder)</h3>
            <div className="merge-files-list">
              {files.map((file, index) => (
                <div key={file.id} className="merge-file-item">
                  <div className="file-order">{index + 1}</div>
                  <div className="file-icon">📄</div>
                  <div className="file-details">
                    <div className="file-name">{file.name}</div>
                    <div className="file-pages">{file.pageCount} page{file.pageCount !== 1 ? 's' : ''}</div>
                  </div>
                  <div className="file-actions">
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === files.length - 1}
                      title="Move down"
                    >
                      ↓
                    </button>
                    <button
                      className="btn btn-ghost btn-sm remove-btn"
                      onClick={() => handleRemoveFile(file.id)}
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {files.length >= 2 && (
          <div className="output-section">
            <label>
              <span>Output filename:</span>
              <div className="filename-input">
                <input
                  type="text"
                  value={outputFilename}
                  onChange={(e) => setOutputFilename(e.target.value)}
                  placeholder="merged"
                />
                <span className="extension">.pdf</span>
              </div>
            </label>
          </div>
        )}
      </div>

      <div className="merger-footer">
        <button
          className="btn btn-success"
          onClick={handleMerge}
          disabled={isMerging || files.length < 2}
        >
          {isMerging ? (
            <>
              <span className="spinner"></span>
              Merging...
            </>
          ) : (
            `Merge ${files.length} PDFs into One`
          )}
        </button>
      </div>
    </div>
  );
};

export default PDFMerger;
